import { Mirror, type Cartridge } from "./Cartridge"
import { u16 } from "./utils"

type Pixel = [number, number, number]

enum Status {
    Unused = 1 << 0,
    SpriteOverflow = 1 << 5,
    SpriteZeroHit = 1 << 6,
    VerticalBlank = 1 << 7,
}

enum Mask {
    Grayscale = 1 << 0,
    RenderBackgroundLeft = 1 << 1,
    RenderSpritesLeft = 1 << 2,
    RenderBackground = 1 << 3,
    RenderSprites = 1 << 4,
    EnhanceRed = 1 << 5,
    EnhanceGreen = 1 << 6,
    EnhanceBlue = 1 << 7,
}

enum Control {
    NametableX = 1 << 0,
    NametableY = 1 << 1,
    IncrementMode = 1 << 2,
    PatternSprite = 1 << 3,
    PatternBackground = 1 << 4,
    SpriteSize = 1 << 5,
    SlaveMode = 1 << 6,
    EnableNmi = 1 << 7,
}

class LoopyRegister {
    register = 0x0000

    private get(offset: number, width: number) {
        return (this.register >> offset) & ((1 << width) - 1)
    }

    private set(offset: number, width: number, v: number) {
        const mask = ((1 << width) - 1) << offset
        this.register = (this.register & ~mask) | ((v & ((1 << width) - 1)) << offset)
    }

    get coarseX() {
        return this.get(0, 5)
    }
    set coarseX(v: number) {
        this.set(0, 5, v)
    }

    get coarseY() {
        return this.get(5, 5)
    }
    set coarseY(v: number) {
        this.set(5, 5, v)
    }

    get nametableX() {
        return this.get(10, 1)
    }
    set nametableX(v: number) {
        this.set(10, 1, v)
    }

    get nametableY() {
        return this.get(11, 1)
    }
    set nametableY(v: number) {
        this.set(11, 1, v)
    }

    get fineY() {
        return this.get(12, 3)
    }
    set fineY(v: number) {
        this.set(12, 3, v)
    }

    get unused() {
        return this.get(15, 1)
    }
    set unused(v: number) {
        this.set(15, 1, v)
    }
}

export class Ppu2C02 {
    readonly SCREEN_WIDTH = 256
    readonly SCREEN_HEIGHT = 240

    private tableName: [Uint8Array, Uint8Array] = [new Uint8Array(1024), new Uint8Array(1024)]
    private tablePattern: [Uint8Array, Uint8Array] = [new Uint8Array(4 * 1024), new Uint8Array(4 * 1024)]
    private tablePalette = new Uint8Array(32)

    private statusRegister = 0x00
    private maskRegister = 0x00
    private controlRegister = 0x00
    private vramAddress = new LoopyRegister()
    private tramAddress = new LoopyRegister()

    private fineX = 0x00
    private addressLatch = 0x00
    private dataBuffer = 0x00

    private scanline = 0
    private cycle = 0

    private bgNextTileId = 0x00
    private bgNextTileAttribute = 0x00
    private bgNextTileLsb = 0b0
    private bgNextTileMsb = 0b0
    private bgShifterPatternLow = 0x0000
    private bgShifterPatternHigh = 0x0000
    private bgShifterAttributeLow = 0x0000
    private bgShifterAttributeHigh = 0x0000

    oam = new Uint8Array(4 * 64)
    private oamAddress = 0x00

    private spriteScanline = new Uint8Array(4 * 8)
    private spriteCount = 0
    private spriteShifterPatternLow = new Uint8Array(8)
    private spriteShifterPatternHigh = new Uint8Array(8)

    private spriteZeroHitPossible = false
    private spriteZeroBeingRendered = false

    private cartridge!: Cartridge

    nmi = false

    frameBuffer = new Uint8ClampedArray(this.SCREEN_WIDTH * this.SCREEN_HEIGHT * 4)
    frameComplete = false

    private paletteScreen = this.getPaletteScreen()

    private setPixel(x: number, y: number, pixel: Pixel) {
        if (x >= this.SCREEN_WIDTH || y >= this.SCREEN_HEIGHT) return
        const index = (y * this.SCREEN_WIDTH + x) * 4

        this.frameBuffer[index] = pixel[0]
        this.frameBuffer[index + 1] = pixel[1]
        this.frameBuffer[index + 2] = pixel[2]
        this.frameBuffer[index + 3] = 0xff
    }

    cpuRead(address: number) {
        let data = 0x00

        switch (address) {
            case 0x0000: // Control
                break
            case 0x0001: // Mask
                break
            case 0x0002: // Status
                data = (this.statusRegister & 0xe0) | (this.dataBuffer & 0x1f)

                this.statusRegister &= ~Status.VerticalBlank

                this.addressLatch = 0
                break
            case 0x0003: // OAM Address
                break
            case 0x0004: // OAM Data
                data = this.oam[this.oamAddress]!
                break
            case 0x0005: // Scroll
                break
            case 0x0006: // PPU Address
                break
            case 0x0007: // PPU Data
                data = this.dataBuffer
                this.dataBuffer = this.ppuRead(this.vramAddress.register)
                if (this.vramAddress.register >= 0x3f00) data = this.dataBuffer

                this.vramAddress.register = u16(
                    this.vramAddress.register + (this.controlRegister & Control.IncrementMode ? 32 : 1),
                )
                break
        }

        return data
    }

    cpuWrite(address: number, data: number) {
        switch (address) {
            case 0x0000: // Control
                this.controlRegister = data
                this.tramAddress.nametableX = this.controlRegister & Control.NametableX ? 1 : 0
                this.tramAddress.nametableY = this.controlRegister & Control.NametableY ? 1 : 0
                break
            case 0x0001: // Mask
                this.maskRegister = data
                break
            case 0x0002: // Status
                break
            case 0x0003: // OAM Address
                this.oamAddress = data
                break
            case 0x0004: // OAM Data
                this.oam[this.oamAddress] = data
                break
            case 0x0005: // Scroll
                if (this.addressLatch === 0) {
                    this.fineX = data & 0x07
                    this.tramAddress.coarseX = data >> 3
                    this.addressLatch = 1
                } else {
                    this.tramAddress.fineY = data & 0x07
                    this.tramAddress.coarseY = data >> 3
                    this.addressLatch = 0
                }
                break
            case 0x0006: // PPU Address
                if (this.addressLatch == 0) {
                    this.tramAddress.register = ((data & 0x3f) << 8) | (this.tramAddress.register & 0x00ff)
                    this.addressLatch = 1
                } else {
                    this.tramAddress.register = (this.tramAddress.register & 0xff00) | data
                    this.vramAddress.register = this.tramAddress.register
                    this.addressLatch = 0
                }
                break
            case 0x0007: // PPU Data
                this.ppuWrite(this.vramAddress.register, data)
                this.vramAddress.register = u16(
                    this.vramAddress.register + (this.controlRegister & Control.IncrementMode ? 32 : 1),
                )
                break
        }
    }

    ppuRead(address: number) {
        let data = this.cartridge.ppuRead(address)
        address &= 0x3fff

        if (data !== undefined) {
        } else if (address >= 0x0000 && address <= 0x1fff) {
            data = this.tablePattern[(address & 0x1000) >> 12]![address & 0x0fff]
        } else if (address >= 0x2000 && address <= 0x3eff) {
            address &= 0x0fff

            if (this.cartridge.mirror() === Mirror.Vertical) {
                if (address >= 0x0000 && address <= 0x03ff) data = this.tableName[0][address & 0x03ff]
                if (address >= 0x0400 && address <= 0x07ff) data = this.tableName[1][address & 0x03ff]
                if (address >= 0x0800 && address <= 0x0bff) data = this.tableName[0][address & 0x03ff]
                if (address >= 0x0c00 && address <= 0x0fff) data = this.tableName[1][address & 0x03ff]
            } else if (this.cartridge.mirror() === Mirror.Horizontal) {
                if (address >= 0x0000 && address <= 0x03ff) data = this.tableName[0][address & 0x03ff]
                if (address >= 0x0400 && address <= 0x07ff) data = this.tableName[0][address & 0x03ff]
                if (address >= 0x0800 && address <= 0x0bff) data = this.tableName[1][address & 0x03ff]
                if (address >= 0x0c00 && address <= 0x0fff) data = this.tableName[1][address & 0x03ff]
            } else if (this.cartridge.mirror() === Mirror.OneScreenLow) {
                data = this.tableName[0][address & 0x03ff]
            } else if (this.cartridge.mirror() === Mirror.OneScreenHigh) {
                data = this.tableName[1][address & 0x03ff]
            }
        } else if (address >= 0x3f00 && address <= 0x3fff) {
            address &= 0x001f
            if (address === 0x0010) address = 0x0000
            if (address === 0x0014) address = 0x0004
            if (address === 0x0018) address = 0x0008
            if (address === 0x001c) address = 0x000c
            data = (this.tablePalette[address] ?? 0) & (this.maskRegister & Mask.Grayscale ? 0x30 : 0x3f)
        }

        return data ?? 0x00
    }

    ppuWrite(address: number, data: number) {
        address &= 0x3fff

        if (this.cartridge.ppuWrite(address, data)) {
        } else if (address >= 0x0000 && address <= 0x1fff) {
            this.tablePattern[(address & 0x1000) >> 12]![address & 0x0fff] = data
        } else if (address >= 0x2000 && address <= 0x3eff) {
            address &= 0x0fff

            if (this.cartridge.mirror() === Mirror.Vertical) {
                if (address >= 0x0000 && address <= 0x03ff) this.tableName[0][address & 0x03ff] = data
                if (address >= 0x0400 && address <= 0x07ff) this.tableName[1][address & 0x03ff] = data
                if (address >= 0x0800 && address <= 0x0bff) this.tableName[0][address & 0x03ff] = data
                if (address >= 0x0c00 && address <= 0x0fff) this.tableName[1][address & 0x03ff] = data
            } else if (this.cartridge.mirror() === Mirror.Horizontal) {
                if (address >= 0x0000 && address <= 0x03ff) this.tableName[0][address & 0x03ff] = data
                if (address >= 0x0400 && address <= 0x07ff) this.tableName[0][address & 0x03ff] = data
                if (address >= 0x0800 && address <= 0x0bff) this.tableName[1][address & 0x03ff] = data
                if (address >= 0x0c00 && address <= 0x0fff) this.tableName[1][address & 0x03ff] = data
            } else if (this.cartridge.mirror() === Mirror.OneScreenLow) {
                this.tableName[0][address & 0x03ff] = data
            } else if (this.cartridge.mirror() === Mirror.OneScreenHigh) {
                this.tableName[1][address & 0x03ff] = data
            }
        } else if (address >= 0x3f00 && address <= 0x3fff) {
            address &= 0x001f
            if (address === 0x0010) address = 0x0000
            if (address === 0x0014) address = 0x0004
            if (address === 0x0018) address = 0x0008
            if (address === 0x001c) address = 0x000c

            this.tablePalette[address] = data
        }
    }

    connectCartridge(cartridge: Cartridge) {
        this.cartridge = cartridge
    }

    reset() {
        this.fineX = 0x00
        this.addressLatch = 0x00
        this.dataBuffer = 0x00
        this.scanline = 0
        this.cycle = 0
        this.bgNextTileId = 0x00
        this.bgNextTileAttribute = 0x00
        this.bgNextTileLsb = 0x00
        this.bgNextTileMsb = 0x00
        this.bgShifterPatternLow = 0x0000
        this.bgShifterPatternHigh = 0x0000
        this.bgShifterAttributeLow = 0x0000
        this.bgShifterAttributeHigh = 0x0000
        this.statusRegister = 0x00
        this.maskRegister = 0x00
        this.controlRegister = 0x00
        this.vramAddress.register = 0x0000
        this.tramAddress.register = 0x0000
    }

    clock() {
        if (this.scanline >= -1 && this.scanline < this.SCREEN_HEIGHT) {
            if (this.scanline === 0 && this.cycle === 0) this.cycle = 1
            if (this.scanline === -1 && this.cycle === 1) {
                this.statusRegister &= ~Status.VerticalBlank
                this.statusRegister &= ~Status.SpriteZeroHit
                this.statusRegister &= ~Status.SpriteOverflow
            }

            if ((this.cycle >= 2 && this.cycle < 258) || (this.cycle >= 321 && this.cycle < 338)) {
                this.updateShifters()

                switch ((this.cycle - 1) % 8) {
                    case 0:
                        this.loadBackgroundShifters()
                        this.bgNextTileId = this.ppuRead(0x2000 | (this.vramAddress.register & 0x0fff))
                        break
                    case 2:
                        this.bgNextTileAttribute = this.ppuRead(
                            0x23c0 |
                                (this.vramAddress.nametableY << 11) |
                                (this.vramAddress.nametableX << 10) |
                                ((this.vramAddress.coarseY >> 2) << 3) |
                                (this.vramAddress.coarseX >> 2),
                        )

                        if (this.vramAddress.coarseY & 0x02) this.bgNextTileAttribute >>= 4
                        if (this.vramAddress.coarseX & 0x02) this.bgNextTileAttribute >>= 2
                        this.bgNextTileAttribute &= 0x03
                        break
                    case 4:
                        this.bgNextTileLsb = this.ppuRead(
                            u16(
                                (((this.controlRegister & Control.PatternBackground) >> 4) << 12) +
                                    (this.bgNextTileId << 4) +
                                    this.vramAddress.fineY,
                            ),
                        )
                        break
                    case 6:
                        this.bgNextTileMsb = this.ppuRead(
                            u16(
                                (((this.controlRegister & Control.PatternBackground) >> 4) << 12) +
                                    (this.bgNextTileId << 4) +
                                    (this.vramAddress.fineY + 8),
                            ),
                        )
                        break
                    case 7:
                        this.incrementScrollX()
                        break
                }
            }

            if (this.cycle === this.SCREEN_WIDTH) {
                this.incrementScrollY()
            }

            if (this.cycle === 257) {
                this.loadBackgroundShifters()
                this.transferAddressX()
            }

            if (this.cycle === 338 || this.cycle === 340) {
                this.bgNextTileId = this.ppuRead(0x2000 | (this.vramAddress.register & 0x0fff))
            }

            if (this.scanline === -1 && this.cycle >= 280 && this.cycle < 305) {
                this.transferAddressY()
            }

            if (this.cycle === 257 && this.scanline >= 0) {
                this.spriteScanline.forEach((_v, i, array) => (array[i] = 0xff))
                this.spriteCount = 0

                for (let i = 0; i < 8; i++) {
                    this.spriteShifterPatternLow[i] = 0
                    this.spriteShifterPatternHigh[i] = 0
                }

                let oamIndex = 0
                this.spriteZeroHitPossible = false

                while (oamIndex < 64 && this.spriteCount < 9) {
                    const diff = u16(this.scanline - this.oam[oamIndex * 4]!)

                    if (diff >= 0 && diff < (this.controlRegister & Control.SpriteSize ? 16 : 8)) {
                        if (this.spriteCount < 8) {
                            if (oamIndex === 0) this.spriteZeroHitPossible = true
                            for (let i = 0; i < 4; i++) {
                                this.spriteScanline[this.spriteCount * 4 + i] = this.oam[oamIndex * 4 + i]!
                            }
                            this.spriteCount++
                        }
                    }

                    oamIndex++
                }

                if (this.spriteCount > 8) this.statusRegister |= Status.SpriteOverflow
            }

            if (this.cycle === 340) {
                for (let i = 0; i < this.spriteCount; i++) {
                    let spritePatternBitsLow = 0x00
                    let spritePatternBitsHigh = 0x00
                    let spritePatternAddressLow = 0x0000
                    let spritePatternAddressHigh = 0x0000

                    if (!(this.controlRegister & Control.SpriteSize)) {
                        if (!(this.spriteScanline[i * 4 + 2]! & 0x80)) {
                            spritePatternAddressLow =
                                (((this.controlRegister & Control.PatternSprite) >> 3) << 12) |
                                (this.spriteScanline[i * 4 + 1]! << 4) |
                                (this.scanline - this.spriteScanline[i * 4]!)
                        } else {
                            spritePatternAddressLow =
                                (((this.controlRegister & Control.PatternSprite) >> 3) << 12) |
                                (this.spriteScanline[i * 4 + 1]! << 4) |
                                (7 - (this.scanline - this.spriteScanline[i * 4]!))
                        }
                    } else {
                        if (!(this.spriteScanline[i * 4 + 2]! & 0x80)) {
                            if (this.scanline - this.spriteScanline[i * 4]! < 8) {
                                spritePatternAddressLow =
                                    ((this.spriteScanline[i * 4 + 1]! & 0x01) << 12) |
                                    ((this.spriteScanline[i * 4 + 1]! & 0xfe) << 4) |
                                    ((this.scanline - this.spriteScanline[i * 4]!) & 0x07)
                            } else {
                                spritePatternAddressLow =
                                    ((this.spriteScanline[i * 4 + 1]! & 0x01) << 12) |
                                    (((this.spriteScanline[i * 4 + 1]! & 0xfe) + 1) << 4) |
                                    ((this.scanline - this.spriteScanline[i * 4]!) & 0x07)
                            }
                        } else {
                            if (this.scanline - this.spriteScanline[i * 4]! < 8) {
                                spritePatternAddressLow =
                                    ((this.spriteScanline[i * 4 + 1]! & 0x01) << 12) |
                                    (((this.spriteScanline[i * 4 + 1]! & 0xfe) + 1) << 4) |
                                    ((7 - (this.scanline - this.spriteScanline[i * 4]!)) & 0x07)
                            } else {
                                spritePatternAddressLow =
                                    ((this.spriteScanline[i * 4 + 1]! & 0x01) << 12) |
                                    ((this.spriteScanline[i * 4 + 1]! & 0xfe) << 4) |
                                    ((7 - (this.scanline - this.spriteScanline[i * 4]!)) & 0x07)
                            }
                        }
                    }

                    spritePatternAddressHigh = spritePatternAddressLow + 8

                    spritePatternBitsLow = this.ppuRead(spritePatternAddressLow)
                    spritePatternBitsHigh = this.ppuRead(spritePatternAddressHigh)

                    if (this.spriteScanline[i * 4 + 2]! & 0x40) {
                        spritePatternBitsLow = this.flipByte(spritePatternBitsLow)
                        spritePatternBitsHigh = this.flipByte(spritePatternBitsHigh)
                    }

                    this.spriteShifterPatternLow[i] = spritePatternBitsLow
                    this.spriteShifterPatternHigh[i] = spritePatternBitsHigh
                }
            }
        }

        if (this.scanline === this.SCREEN_HEIGHT) {
        }

        if (this.scanline >= 241 && this.scanline < 261) {
            if (this.scanline === 241 && this.cycle === 1) {
                this.statusRegister |= Status.VerticalBlank

                if (this.controlRegister & Control.EnableNmi) this.nmi = true
            }
        }

        let backgroundPixel = 0x00
        let backgroundPalette = 0x00

        if (this.maskRegister & Mask.RenderBackground) {
            const bitMux = 0x8000 >> this.fineX
            const plane0Pixel = (this.bgShifterPatternLow & bitMux) > 0
            const plane1Pixel = (this.bgShifterPatternHigh & bitMux) > 0

            backgroundPixel = (Number(plane1Pixel) << 1) | Number(plane0Pixel)

            const backgroundPalette0 = (this.bgShifterAttributeLow & bitMux) > 0
            const backgroundPalette1 = (this.bgShifterAttributeHigh & bitMux) > 0
            backgroundPalette = (Number(backgroundPalette1) << 1) | Number(backgroundPalette0)
        }

        let foregroundPixel = 0x00
        let foregroundPalette = 0x00
        let foregroundPriority = false

        if (this.maskRegister & Mask.RenderSprites) {
            this.spriteZeroBeingRendered = false

            for (let i = 0; i < this.spriteCount; i++) {
                if (this.spriteScanline[i * 4 + 3] !== 0) continue

                const foregroundPixelLow = (this.spriteShifterPatternLow[i]! & 0x80) > 0
                const foregroundPixelHigh = (this.spriteShifterPatternHigh[i]! & 0x80) > 0
                foregroundPixel = (Number(foregroundPixelHigh) << 1) | Number(foregroundPixelLow)

                foregroundPalette = (this.spriteScanline[i * 4 + 2]! & 0x03) + 0x04
                foregroundPriority = (this.spriteScanline[i * 4 + 2]! & 0x20) === 0

                if (foregroundPixel !== 0) {
                    if (this.spriteZeroHitPossible && i === 0) this.spriteZeroBeingRendered = true
                    break
                }
            }
        }

        let pixel = 0x00
        let palette = 0x00

        if (backgroundPixel === 0 && foregroundPixel === 0) {
            pixel = 0x00
            palette = 0x00
        } else if (backgroundPixel === 0 && foregroundPixel > 0) {
            pixel = foregroundPixel
            palette = foregroundPalette
        } else if (backgroundPixel > 0 && foregroundPixel === 0) {
            pixel = backgroundPixel
            palette = backgroundPalette
        } else if (backgroundPixel > 0 && foregroundPixel > 0) {
            if (foregroundPriority) {
                pixel = foregroundPixel
                palette = foregroundPalette
            } else {
                pixel = backgroundPixel
                palette = backgroundPalette
            }

            if (this.spriteZeroHitPossible && this.spriteZeroBeingRendered) {
                if (this.maskRegister & Mask.RenderBackground && this.maskRegister & Mask.RenderSprites) {
                    if (
                        !(this.maskRegister & Mask.RenderBackgroundLeft) &&
                        !(this.maskRegister & Mask.RenderSpritesLeft)
                    ) {
                        if (this.cycle >= 9 && this.cycle < 258) {
                            this.statusRegister |= Status.SpriteZeroHit
                        }
                    } else {
                        if (this.cycle >= 1 && this.cycle < 258) {
                            this.statusRegister |= Status.SpriteZeroHit
                        }
                    }
                }
            }
        }

        if (
            this.cycle >= 1 &&
            this.cycle <= 256 &&
            this.scanline >= 0 &&
            this.scanline < this.SCREEN_HEIGHT
        ) {
            this.setPixel(this.cycle - 1, this.scanline, this.getColourFromPaletteRam(palette, pixel))
        }

        this.cycle++
        if (this.cycle >= 341) {
            this.cycle = 0
            this.scanline++
            if (this.scanline >= 261) {
                this.scanline = -1
                this.frameComplete = true
            }
        }
    }

    private incrementScrollX() {
        if (!(this.maskRegister & Mask.RenderBackground || this.maskRegister & Mask.RenderSprites)) return

        if (this.vramAddress.coarseX === 31) {
            this.vramAddress.coarseX = 0
            this.vramAddress.nametableX = ~this.vramAddress.nametableX & 0x01
        } else {
            this.vramAddress.coarseX++
        }
    }

    private incrementScrollY() {
        if (!(this.maskRegister & Mask.RenderBackground || this.maskRegister & Mask.RenderSprites)) return

        if (this.vramAddress.fineY < 7) {
            this.vramAddress.fineY++
        } else {
            this.vramAddress.fineY = 0
            if (this.vramAddress.coarseY === 29) {
                this.vramAddress.coarseY = 0
                this.vramAddress.nametableY = ~this.vramAddress.nametableY & 0x01
            } else if (this.vramAddress.coarseY === 31) {
                this.vramAddress.coarseY = 0
            } else {
                this.vramAddress.coarseY++
            }
        }
    }

    private transferAddressX() {
        if (!(this.maskRegister & Mask.RenderBackground || this.maskRegister & Mask.RenderSprites)) return

        this.vramAddress.nametableX = this.tramAddress.nametableX
        this.vramAddress.coarseX = this.tramAddress.coarseX
    }

    private transferAddressY() {
        if (!(this.maskRegister & Mask.RenderBackground || this.maskRegister & Mask.RenderSprites)) return

        this.vramAddress.fineY = this.tramAddress.fineY
        this.vramAddress.nametableY = this.tramAddress.nametableY
        this.vramAddress.coarseY = this.tramAddress.coarseY
    }

    private loadBackgroundShifters() {
        this.bgShifterPatternLow = (this.bgShifterPatternLow & 0xff00) | this.bgNextTileLsb
        this.bgShifterPatternHigh = (this.bgShifterPatternHigh & 0xff00) | this.bgNextTileMsb

        this.bgShifterAttributeLow =
            (this.bgShifterAttributeLow & 0xff00) | (this.bgNextTileAttribute & 0b01 ? 0xff : 0x00)
        this.bgShifterAttributeHigh =
            (this.bgShifterAttributeHigh & 0xff00) | (this.bgNextTileAttribute & 0b10 ? 0xff : 0x00)
    }

    private updateShifters() {
        if (this.maskRegister & Mask.RenderBackground) {
            this.bgShifterPatternLow <<= 1
            this.bgShifterPatternHigh <<= 1

            this.bgShifterAttributeLow <<= 1
            this.bgShifterAttributeHigh <<= 1
        }

        if (this.maskRegister & Mask.RenderSprites && this.cycle >= 1 && this.cycle < 258) {
            for (let i = 0; i < this.spriteCount; i++) {
                if (this.spriteScanline[i * 4 + 3]! > 0) {
                    this.spriteScanline[i * 4 + 3]!--
                } else {
                    this.spriteShifterPatternLow[i]! <<= 1
                    this.spriteShifterPatternHigh[i]! <<= 1
                }
            }
        }
    }

    private flipByte = (byte: number) => {
        byte = ((byte & 0xf0) >> 4) | ((byte & 0x0f) << 4)
        byte = ((byte & 0xcc) >> 2) | ((byte & 0x33) << 2)
        byte = ((byte & 0xaa) >> 1) | ((byte & 0x55) << 1)
        return byte
    }

    getColourFromPaletteRam(palette: number, pixel: number) {
        return this.paletteScreen[this.ppuRead(u16(0x3f00 + (palette << 2) + pixel)) & 0x3f]!
    }

    getPaletteRam() {
        return this.tablePalette
    }

    private getPaletteScreen() {
        const paletteScreen: Pixel[] = []

        paletteScreen[0x00] = [84, 84, 84]
        paletteScreen[0x01] = [0, 30, 116]
        paletteScreen[0x02] = [8, 16, 144]
        paletteScreen[0x03] = [48, 0, 136]
        paletteScreen[0x04] = [68, 0, 100]
        paletteScreen[0x05] = [92, 0, 48]
        paletteScreen[0x06] = [84, 4, 0]
        paletteScreen[0x07] = [60, 24, 0]
        paletteScreen[0x08] = [32, 42, 0]
        paletteScreen[0x09] = [8, 58, 0]
        paletteScreen[0x0a] = [0, 64, 0]
        paletteScreen[0x0b] = [0, 60, 0]
        paletteScreen[0x0c] = [0, 50, 60]
        paletteScreen[0x0d] = [0, 0, 0]
        paletteScreen[0x0e] = [0, 0, 0]
        paletteScreen[0x0f] = [0, 0, 0]

        paletteScreen[0x10] = [152, 150, 152]
        paletteScreen[0x11] = [8, 76, 196]
        paletteScreen[0x12] = [48, 50, 236]
        paletteScreen[0x13] = [92, 30, 228]
        paletteScreen[0x14] = [136, 20, 176]
        paletteScreen[0x15] = [160, 20, 100]
        paletteScreen[0x16] = [152, 34, 32]
        paletteScreen[0x17] = [120, 60, 0]
        paletteScreen[0x18] = [84, 90, 0]
        paletteScreen[0x19] = [40, 114, 0]
        paletteScreen[0x1a] = [8, 124, 0]
        paletteScreen[0x1b] = [0, 118, 40]
        paletteScreen[0x1c] = [0, 102, 120]
        paletteScreen[0x1d] = [0, 0, 0]
        paletteScreen[0x1e] = [0, 0, 0]
        paletteScreen[0x1f] = [0, 0, 0]

        paletteScreen[0x20] = [236, 238, 236]
        paletteScreen[0x21] = [76, 154, 236]
        paletteScreen[0x22] = [120, 124, 236]
        paletteScreen[0x23] = [176, 98, 236]
        paletteScreen[0x24] = [228, 84, 236]
        paletteScreen[0x25] = [236, 88, 180]
        paletteScreen[0x26] = [236, 106, 100]
        paletteScreen[0x27] = [212, 136, 32]
        paletteScreen[0x28] = [160, 170, 0]
        paletteScreen[0x29] = [116, 196, 0]
        paletteScreen[0x2a] = [76, 208, 32]
        paletteScreen[0x2b] = [56, 204, 108]
        paletteScreen[0x2c] = [56, 180, 204]
        paletteScreen[0x2d] = [60, 60, 60]
        paletteScreen[0x2e] = [0, 0, 0]
        paletteScreen[0x2f] = [0, 0, 0]

        paletteScreen[0x30] = [236, 238, 236]
        paletteScreen[0x31] = [168, 204, 236]
        paletteScreen[0x32] = [188, 188, 236]
        paletteScreen[0x33] = [212, 178, 236]
        paletteScreen[0x34] = [236, 174, 236]
        paletteScreen[0x35] = [236, 174, 212]
        paletteScreen[0x36] = [236, 180, 176]
        paletteScreen[0x37] = [228, 196, 144]
        paletteScreen[0x38] = [204, 210, 120]
        paletteScreen[0x39] = [180, 222, 120]
        paletteScreen[0x3a] = [168, 226, 144]
        paletteScreen[0x3b] = [152, 226, 180]
        paletteScreen[0x3c] = [160, 214, 228]
        paletteScreen[0x3d] = [160, 162, 160]
        paletteScreen[0x3e] = [0, 0, 0]
        paletteScreen[0x3f] = [0, 0, 0]

        return paletteScreen
    }
}
